import React, { useEffect, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    FormHelperText,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { MdArrowBack, MdPerson, MdSecurity } from 'react-icons/md';
import {
    FaUser,
    FaCheckCircle,
    FaTimesCircle,
    FaCloudUploadAlt,
    FaTimes,
} from 'react-icons/fa';
import { getData } from '../../Webservice/Webservice';

// A bordered card with a coloured top edge, an icon and a hint line.
const UserSection = ({ title, hint, accent, icon: Icon, children }) => (
    <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
            <Box>
                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: 15, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 12, color: '#64748b' }}>{hint}</Typography>
            </Box>
        </Box>
        {children}
    </Paper>
);

const AddUserModal = ({
    open,
    handleClose,
    newUser,
    handleChange,
    handleSubmit,
    isEdit = false,
    editingUser = null,
    onPermissionsLoaded,
    asPage = false,
}) => {
    const [roles, setRoles] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);
    const [permissions, setPermissions] = useState([]); // structured permissions for building options
    const [warehouseError, setWarehouseError] = useState('');

    // Validation state
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');

    // Helper function to flatten permissions for Autocomplete
    const getAllPermissions = (permissionsList) => {
        const allPermissions = [];
        permissionsList.forEach((perm) => {
            allPermissions.push(perm.name);
            if (perm.children) {
                perm.children.forEach((child) => {
                    allPermissions.push(`${perm.name} > ${child}`);
                });
            }
        });
        return allPermissions;
    };

    const allPermissions = getAllPermissions(permissions);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                const imageUrl = ev.target.result;
                setImagePreview(imageUrl);
                // keep original behavior: notify parent with preview/base64 (if your API expects File/FormData, adapt parent)
                handleChange({
                    target: {
                        name: 'image',
                        value: imageUrl
                    }
                });
                // clear any image related error
                setErrors(prev => ({ ...prev, image: undefined }));
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImagePreview(null);
        handleChange({
            target: {
                name: 'image',
                value: ''
            }
        });
    };

    useEffect(() => {
        if (!open) {
            // reset preview & errors on close
            setImagePreview(null);
            setWarehouseError('');
            setErrors({});
            setFormError('');
            return;
        }

        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';

        // Async loader that fetches roles, menus, warehouses and optionally user detail
        const loadInitialData = async () => {
            try {
                // Fetch roles, menus and warehouses in parallel
                const [rolesRes, menusRes, warehousesRes] = await Promise.all([
                    getData('role/list?limit=100', null, token).catch(() => null),
                    getData('menu/list', undefined, token).catch(() => null),
                    getData('warehouse/list', { page: 1, limit: 100 }, token).catch(() => null)
                ]);

                if (rolesRes) {
                    const rList = Array.isArray(rolesRes) ? rolesRes : (rolesRes.roles || []);
                    setRoles(rList);
                }

                if (menusRes && Array.isArray(menusRes)) {
                    if (typeof onPermissionsLoaded === 'function') {
                        onPermissionsLoaded(menusRes);
                    }
                    const perms = menusRes.map((menu) => ({
                        name: menu.name,
                        children: menu.submenus && menu.submenus.length > 0 ? menu.submenus.map((sub) => sub.name) : undefined,
                        _raw: menu, // keep raw to help mapping from IDs/names if needed
                    }));
                    setPermissions(perms);
                }

                if (warehousesRes) {
                    const wList = Array.isArray(warehousesRes) ? warehousesRes : (warehousesRes.warehouse || []);
                    setWarehouses(wList);
                }

                // If editing, fetch user detail from separate API route and populate form fields
                if (isEdit) {
                    // support editingUser being id string or object
                    const userId = typeof editingUser === 'string' ? editingUser : (editingUser && editingUser._id ? editingUser._id : null);
                    if (userId) {
                        const detailRes = await getData(`user/${userId}`, undefined, token);
                        if (detailRes && detailRes.user) {
                            const u = detailRes.user;

                            // Map fields to parent form state using handleChange
                            handleChange({ target: { name: 'firstName', value: u.firstName || '' } });
                            handleChange({ target: { name: 'lastName', value: u.lastName || '' } });
                            handleChange({ target: { name: 'email', value: u.email || '' } });
                            handleChange({ target: { name: 'phoneNumber', value: u.phoneNumber || '' } });

                            // role_id in response is an object; set to _id if present
                            const roleId = u.role_id && (typeof u.role_id === 'object') ? u.role_id._id : (u.role_id || '');
                            handleChange({ target: { name: 'role_id', value: roleId } });

                            // status
                            handleChange({ target: { name: 'status', value: u.status || 'active' } });

                            // warehouse id - check both warehouseId and warehouse_id, handle objects or strings
                            const rawWarehouse = u.warehouseId || u.warehouse_id || '';
                            const warehouseIdValue = (rawWarehouse && typeof rawWarehouse === 'object') ? rawWarehouse._id : rawWarehouse;
                            handleChange({ target: { name: 'warehouseId', value: warehouseIdValue || '' } });

                            // image - set preview and notify parent value
                            if (u.image) {
                                setImagePreview(u.image);
                                handleChange({ target: { name: 'image', value: u.image } });
                            } else {
                                setImagePreview(null);
                                handleChange({ target: { name: 'image', value: '' } });
                            }

                            // Map accessibleMenus -> flattened permission strings expected by Autocomplete
                            const mappedPermissions = [];
                            if (Array.isArray(detailRes.accessibleMenus)) {
                                detailRes.accessibleMenus.forEach((menuItem) => {
                                    const menuName = menuItem.menuName;

                                    // if any of menuItem.permissions flags are true, include parent menu
                                    const permsObj = menuItem.permissions || {};
                                    const menuHasPermission = Boolean(permsObj.read || permsObj.create || permsObj.update || permsObj.delete);
                                    if (menuHasPermission) {
                                        mappedPermissions.push(menuName);
                                    }

                                    // submenus: include each submenu if its permissions object has any true
                                    if (Array.isArray(menuItem.submenus)) {
                                        menuItem.submenus.forEach((sub) => {
                                            const subPerms = sub.permissions || {};
                                            const subHasPermission = Boolean(subPerms.read || subPerms.create || subPerms.update || subPerms.delete);
                                            if (subHasPermission) {
                                                // add "Parent > Child" format
                                                mappedPermissions.push(`${menuName} > ${sub.submenuName}`);
                                                // ensure parent is included (already pushed above if menuHasPermission false)
                                                if (!menuHasPermission) mappedPermissions.push(menuName);
                                            }
                                        });
                                    }
                                });
                            }

                            // dedupe and set only those options that exist in our flattened options (allPermissions)
                            const unique = Array.from(new Set(mappedPermissions));
                            const filtered = unique.filter((v) => {
                                // if menusRes is not fetched yet allPermissions could be empty; still set mappedPermissions raw in that case
                                if (allPermissions && allPermissions.length > 0) {
                                    return allPermissions.includes(v);
                                }
                                return true;
                            });

                            // set permissions into parent state
                            handleChange({ target: { name: 'permissions', value: filtered } });
                        }
                    }
                } else {
                    // not edit: clear image in parent
                    handleChange({ target: { name: 'image', value: '' } });
                    handleChange({ target: { name: 'permissions', value: [] } });
                }
            } catch (err) {
                // silent: you can add logging here if you want
                // console.error(err);
            }
        };

        loadInitialData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEdit, editingUser, onPermissionsLoaded]);

    // Check if the selected role is warehouse_admin
    const isWarehouseAdmin = roles.find(role => role.name && role.name.trim().toLowerCase() === 'warehouse_admin')?._id === newUser.role_id;

    // Validate warehouseId when role is warehouse_admin
    useEffect(() => {
        if (isWarehouseAdmin && !newUser.warehouseId) {
            setWarehouseError('Please select a warehouse');
        } else {
            setWarehouseError('');
        }

        // Clear warehouseId ONLY when the user explicitly switches away from warehouse_admin role
        // Do NOT clear when editing an existing warehouse_admin (isEdit=true) during initial role load
        if (roles.length > 0 && !isWarehouseAdmin && newUser.warehouseId && !isEdit) {
            handleChange({
                target: {
                    name: 'warehouseId',
                    value: ''
                }
            });
        }
    }, [isWarehouseAdmin, newUser.warehouseId, handleChange, roles, isEdit]);

    // Validate form fields
    const validateForm = () => {
        const newErrors = {};

        // First and Last Name
        if (!newUser.firstName || !newUser.firstName.toString().trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!newUser.lastName || !newUser.lastName.toString().trim()) {
            newErrors.lastName = 'Last name is required';
        }

        // Email
        if (!newUser.email || !newUser.email.toString().trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^\S+@\S+\.\S+$/.test(newUser.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Phone number (normalize and validate 10 digits)
        const rawPhone = (newUser.phoneNumber || '').toString();
        const cleanedPhone = rawPhone.replace(/\D/g, '');
        if (!cleanedPhone) {
            newErrors.phoneNumber = 'Mobile number is required';
        } else if (!/^\d{10}$/.test(cleanedPhone)) {
            newErrors.phoneNumber = 'Enter a valid 10-digit mobile number';
        }

        // Role
        if (!newUser.role_id) {
            newErrors.role_id = 'Please select a role';
        }

        // Warehouse required for warehouse_admin role
        if (isWarehouseAdmin && !newUser.warehouseId) {
            newErrors.warehouseId = 'Please select a warehouse';
        }

        // Password: required on create, optional on edit but must be >= 6 chars if provided
        if (!isEdit) {
            if (!newUser.password) {
                newErrors.password = 'Password is required';
            } else if (newUser.password.length < 6) {
                newErrors.password = 'Password must be at least 6 characters';
            }
        } else if (newUser.password && newUser.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        // set a general form error for UI if needed
        if (Object.keys(newErrors).length > 0) {
            setFormError('Please fix the errors highlighted below.');
        } else {
            setFormError('');
        }

        return Object.keys(newErrors).length === 0;
    };

    // Clear specific errors when newUser changes (so UX updates live)
    useEffect(() => {
        // simple cleanup: if a field has an error and it becomes valid, remove the error
        setErrors(prev => {
            const copy = { ...prev };
            if (copy.firstName && newUser.firstName && newUser.firstName.toString().trim()) delete copy.firstName;
            if (copy.lastName && newUser.lastName && newUser.lastName.toString().trim()) delete copy.lastName;
            if (copy.email && newUser.email && /^\S+@\S+\.\S+$/.test(newUser.email)) delete copy.email;
            const cleanedPhone = (newUser.phoneNumber || '').toString().replace(/\D/g, '');
            if (copy.phoneNumber && cleanedPhone && /^\d{10}$/.test(cleanedPhone)) delete copy.phoneNumber;
            if (copy.role_id && newUser.role_id) delete copy.role_id;
            if (copy.password && (isEdit || (newUser.password && newUser.password.length >= 6))) delete copy.password;
            if (copy.warehouseId && (!isWarehouseAdmin || newUser.warehouseId)) delete copy.warehouseId;
            if (copy.image && (isEdit || newUser.image || imagePreview)) delete copy.image;
            return copy;
        });
        // clear formError if no errors remain
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newUser, imagePreview, isWarehouseAdmin]);

    // Local submit handler - run validation then call parent's handleSubmit
    const onFormSubmit = (e) => {
        // defensive: allow call without event
        if (e && typeof e.preventDefault === 'function') e.preventDefault();

        const ok = validateForm();
        if (!ok) return;

        // Pass the event to parent's handleSubmit so it can call preventDefault or use the event.
        if (typeof handleSubmit === 'function') {
            handleSubmit(e);
        }
    };

    const selectedRole = roles.find((r) => r._id === newUser.role_id);
    const roleLabel = selectedRole ? selectedRole.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';
    const fullName = `${newUser.firstName || ''} ${newUser.lastName || ''}`.trim();
    const initials = `${(newUser.firstName || '')[0] || ''}${(newUser.lastName || '')[0] || ''}`.toUpperCase();
    const grantsEverything = selectedRole && ['super_admin', 'warehouse_admin'].includes(selectedRole.name);

    // "Orders" and "Orders > Online Orders" -> { Orders: ['Online Orders'] }
    const menuGroups = {};
    (newUser.permissions || []).forEach((p) => {
        const [parent, child] = String(p).split(' > ');
        menuGroups[parent] = menuGroups[parent] || [];
        if (child) menuGroups[parent].push(child);
    });

    const content = (
        <Box sx={{ p: asPage ? 0 : 3, background: asPage ? '#f8fafc' : '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={handleClose} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                        {asPage ? <MdArrowBack size={20} /> : <FaTimes size={14} />}
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: 24, color: '#0f172a', lineHeight: 1.2 }}>
                            {isEdit ? 'Edit User' : 'Create User'}
                        </Typography>
                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 13, color: '#64748b' }}>
                            {isEdit ? (fullName || 'Update this person\'s details and access') : 'Add a team member and choose what they can open.'}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <form onSubmit={onFormSubmit} noValidate>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <UserSection title="Personal details" hint="Name and how to reach them" accent="#6366f1" icon={MdPerson}>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="First name" name="firstName" value={newUser.firstName || ''} onChange={handleChange} required fullWidth size="small" error={!!errors.firstName} helperText={errors.firstName} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Last name" name="lastName" value={newUser.lastName || ''} onChange={handleChange} required fullWidth size="small" error={!!errors.lastName} helperText={errors.lastName} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Email" name="email" type="email" value={newUser.email || ''} onChange={handleChange} required fullWidth size="small" error={!!errors.email} helperText={errors.email || 'Used to sign in'} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Mobile number" name="phoneNumber" value={newUser.phoneNumber || ''} onChange={handleChange} required fullWidth size="small" error={!!errors.phoneNumber} helperText={errors.phoneNumber || '10 digits'} inputProps={{ maxLength: 12 }} />
                                    </Grid>
                                </Grid>
                            </UserSection>

                            <UserSection title="Role & access" hint="The role decides which pages this user can open" accent="#10b981" icon={MdSecurity}>
                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            label="Role"
                                            name="role_id"
                                            value={newUser.role_id || ''}
                                            onChange={(e) => {
                                                handleChange(e);
                                                // Auto-assign the role's menus as the default permissions
                                                const roleId = e.target.value;
                                                setTimeout(() => {
                                                    const picked = roles.find((r) => r._id === roleId);
                                                    if (picked && picked.menus && picked.name !== 'super_admin' && picked.name !== 'warehouse_admin') {
                                                        const roleMenuIds = picked.menus.map((m) => (typeof m === 'object' ? m._id.toString() : m.toString()));
                                                        const mappedPermissions = [];
                                                        permissions.forEach((perm) => {
                                                            const rawMenu = perm._raw;
                                                            if (roleMenuIds.includes(rawMenu._id.toString())) mappedPermissions.push(perm.name);
                                                            if (rawMenu.submenus) {
                                                                rawMenu.submenus.forEach((sub) => {
                                                                    if (roleMenuIds.includes(sub._id.toString())) {
                                                                        mappedPermissions.push(`${perm.name} > ${sub.name}`);
                                                                        if (!mappedPermissions.includes(perm.name)) mappedPermissions.push(perm.name);
                                                                    }
                                                                });
                                                            }
                                                        });
                                                        handleChange({ target: { name: 'permissions', value: [...new Set(mappedPermissions)] } });
                                                    } else if (picked && (picked.name === 'super_admin' || picked.name === 'warehouse_admin')) {
                                                        // the backend gives these roles full access
                                                        handleChange({ target: { name: 'permissions', value: [] } });
                                                    }
                                                }, 0);
                                            }}
                                            required
                                            fullWidth
                                            size="small"
                                            error={!!errors.role_id}
                                            helperText={errors.role_id}
                                        >
                                            {roles.length > 0 ? (
                                                roles
                                                    .filter((r) => r.roleFor === 'admin' || (r.name && r.name.trim().toLowerCase() === 'warehouse_admin'))
                                                    .map((role) => (
                                                        <MenuItem key={role._id} value={role._id}>
                                                            {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace(/_/g, ' ')}
                                                        </MenuItem>
                                                    ))
                                            ) : (
                                                <MenuItem disabled>Loading roles...</MenuItem>
                                            )}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField select label="Status" name="status" value={String(newUser.status || 'active').toLowerCase()} onChange={handleChange} required fullWidth size="small" error={!!errors.status} helperText={errors.status || 'Inactive users cannot sign in'}>
                                            <MenuItem value="active">
                                                <Stack direction="row" alignItems="center" spacing={1}><FaCheckCircle style={{ color: '#10b981' }} /><Typography sx={{ fontSize: 14 }}>Active</Typography></Stack>
                                            </MenuItem>
                                            <MenuItem value="inactive">
                                                <Stack direction="row" alignItems="center" spacing={1}><FaTimesCircle style={{ color: '#ef4444' }} /><Typography sx={{ fontSize: 14 }}>Inactive</Typography></Stack>
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                    {isWarehouseAdmin && (
                                        <Grid item xs={12}>
                                            <TextField select label="Warehouse" name="warehouseId" value={newUser.warehouseId || ''} onChange={handleChange} required fullWidth size="small" error={!!(warehouseError || errors.warehouseId)} helperText={warehouseError || errors.warehouseId || 'The warehouse this admin will manage'}>
                                                {warehouses.length > 0 ? (
                                                    warehouses.map((warehouse) => <MenuItem key={warehouse._id} value={warehouse._id}>{warehouse.name}</MenuItem>)
                                                ) : (
                                                    <MenuItem disabled>No warehouses available</MenuItem>
                                                )}
                                            </TextField>
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <TextField label={isEdit ? 'New password' : 'Password'} name="password" type="password" value={newUser.password || ''} onChange={handleChange} required={!isEdit} fullWidth size="small" error={!!errors.password} helperText={errors.password || (isEdit ? 'Leave empty to keep the current password' : 'At least 6 characters')} autoComplete="new-password" />
                                    </Grid>
                                </Grid>

                                {/* what the role gives them */}
                                <Box sx={{ mt: 3 }}>
                                    <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: 13.5, mb: 1 }}>Menus this user will see</Typography>
                                    {!newUser.role_id ? (
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 13, color: '#94a3b8' }}>Choose a role to preview.</Typography>
                                    ) : grantsEverything ? (
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 13, color: '#475569' }}>{roleLabel} has access to every {selectedRole.name === 'warehouse_admin' ? 'warehouse' : 'admin'} menu.</Typography>
                                    ) : Object.keys(menuGroups).length === 0 ? (
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 13, color: '#94a3b8' }}>This role has no menus assigned yet.</Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                            {Object.entries(menuGroups).map(([parent, kids]) => (
                                                <Box key={parent} sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                                                    <Chip size="small" label={parent} sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, background: '#0f172a', color: '#fff' }} />
                                                    {kids.map((k) => <Chip key={k} size="small" label={k} sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600, background: '#f1f5f9', color: '#334155' }} />)}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </UserSection>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: { md: 'sticky' }, top: 16, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                <Box sx={{ position: 'relative', display: 'inline-block', mb: 1.5 }}>
                                    <Avatar src={imagePreview || undefined} sx={{ width: 104, height: 104, bgcolor: '#ffd102', color: '#000', fontWeight: 800, fontSize: 34, boxShadow: '0 4px 12px rgba(15,23,42,.12)' }}>
                                        {initials || <FaUser />}
                                    </Avatar>
                                    <IconButton component="label" size="small" sx={{ position: 'absolute', right: -2, bottom: -2, background: '#0f172a', color: '#fff', '&:hover': { background: '#334155' } }}>
                                        <FaCloudUploadAlt size={14} />
                                        <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                                    </IconButton>
                                    {imagePreview && (
                                        <IconButton onClick={removeImage} size="small" aria-label="remove photo" sx={{ position: 'absolute', left: -2, top: -2, background: '#ef4444', color: '#fff', '&:hover': { background: '#dc2626' } }}>
                                            <FaTimes size={11} />
                                        </IconButton>
                                    )}
                                </Box>
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, fontSize: 17 }}>{fullName || 'New user'}</Typography>
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 12.5, color: '#64748b', wordBreak: 'break-word' }}>{newUser.email || 'email'}</Typography>
                                {roleLabel && <Chip size="small" label={roleLabel} sx={{ mt: 1, fontFamily: 'Mulish, sans-serif', fontWeight: 700, background: '#fef3c7', color: '#92400e' }} />}
                                {errors.image && <FormHelperText error sx={{ textAlign: 'center' }}>{errors.image}</FormHelperText>}
                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: 11.5, color: '#94a3b8', mt: 1 }}>Photo is optional</Typography>
                            </Paper>

                            {formError && <Alert severity="error">{formError}</Alert>}

                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isWarehouseAdmin && !newUser.warehouseId}
                                sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none', background: 'rgb(255 209 2)', color: 'black', py: 1.2, borderRadius: '12px', fontWeight: 800, fontSize: 14, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}
                            >
                                {isEdit ? 'Update user' : 'Create user'}
                            </Button>
                            <Button onClick={handleClose} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );

    if (asPage) {
        return (
            <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
                <Box sx={{ maxWidth: 1100, mx: 'auto' }}>{content}</Box>
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px' } }}
        >
            {content}
        </Dialog>
    );
};

export default AddUserModal;
